import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiSend,
  FiLoader,
  FiCheckCircle,
} from "react-icons/fi";

gsap.registerPlugin(ScrollTrigger);

interface FloatingLabelInputProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  required?: boolean;
}

// Floating Label Input Component
const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.length > 0;

  return (
    <div className="relative">
      <label
        htmlFor={id}
        className={`pointer-events-none absolute left-4 transition-all duration-300 ease-in-out
          ${
            isFocused || hasValue
              ? "top-2 text-xs text-nature-green-dark"
              : "top-1/2 -translate-y-1/2 text-gray-500"
          }`}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        required={required}
        className="w-full rounded-lg border-2 border-stone-200 bg-stone-100 px-4 pb-2 pt-6 transition-colors duration-300 focus:border-amber-500 focus:outline-none"
      />
    </div>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formState, setFormState] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [result, setResult] = useState("");

  const sectionRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const mapPinRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    if (infoRef.current) {
      tl.from(infoRef.current.children, {
        opacity: 0,
        x: -50,
        stagger: 0.2,
        duration: 0.8,
        ease: "power3.out",
      });
    }

    if (formRef.current) {
      tl.from(
        formRef.current,
        {
          opacity: 0,
          y: 100,
          duration: 1,
          ease: "power3.out",
        },
        "-=0.5"
      );
    }

    // Pulse map pin
    if (mapPinRef.current) {
      gsap.fromTo(
        mapPinRef.current,
        { scale: 1 },
        {
          scale: 1.1,
          repeat: 1,
          yoyo: true,
          duration: 0.8,
          ease: "power1.inOut",
          delay: 1.5,
          scrollTrigger: {
            trigger: section,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        }
      );
    }

    return () => {
      tl.kill();
      gsap.killTweensOf(mapPinRef.current);
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState("loading");
    setResult("Sending....");

    const form = e.currentTarget;
    const data = new FormData(form);
    data.append("access_key", "YOUR_ACCESS_KEY_HERE"); // IMPORTANT: Replace with your key

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: data,
      });
      const json = await response.json();
      if (json.success) {
        setResult(json.message);
        setFormState("success");
        form.reset();
        setFormData({ name: "", email: "", message: "" });
      } else {
        console.error("Submission error:", json);
        setResult(json.message);
        setFormState("error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setResult("An error occurred. Please try again.");
      setFormState("error");
    }
  };

  return (
    <section ref={sectionRef} className="bg-stone-50 py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 md:grid-cols-2 lg:gap-20">
        {/* Contact Info */}
        <div ref={infoRef} className="space-y-8">
          <div>
            <h2 className="font-serif text-3xl font-semibold text-nature-green-dark md:text-4xl">
              Get in Touch
            </h2>
            <p className="mt-3 text-lg text-gray-600">
              We are here to answer any questions you may have. Reach out to us
              and we'll respond as soon as we can.
            </p>
          </div>
          <div className="space-y-6">
            <a href="mailto:info@nunya.ng" className="group flex items-center">
              <FiMail className="mr-4 h-8 w-8 transform text-amber-600 transition-transform duration-300 group-hover:scale-110" />
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Email</h3>
                <p className="text-gray-600">info@nunya.ng</p>
              </div>
            </a>
            <a href="tel:+1234567890" className="group flex items-center">
              <FiPhone className="mr-4 h-8 w-8 transform text-amber-600 transition-transform duration-300 group-hover:scale-110" />
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Phone</h3>
                <p className="text-gray-600">(123) 456-7890</p>
              </div>
            </a>
            <div className="group flex items-center">
              <span ref={mapPinRef}>
                <FiMapPin className="mr-4 h-8 w-8 text-amber-600" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Location
                </h3>
                <p className="text-gray-600">
                  Nunya, Isuikwuato LGA, Abia State, Nigeria
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div ref={formRef}>
          <div className="rounded-2xl bg-white p-8 shadow-lg md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <FloatingLabelInput
                id="name"
                label="Full Name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <FloatingLabelInput
                id="email"
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div className="relative">
                <label
                  htmlFor="message"
                  className="absolute left-4 top-2 text-xs text-nature-green-dark"
                >
                  Your Message
                </label>
                <textarea
                  id="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full rounded-lg border-2 border-stone-200 bg-stone-100 px-4 pb-2 pt-6 transition-colors duration-300 focus:border-amber-500 focus:outline-none"
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={formState === "loading"}
                className="flex w-full transform items-center justify-center gap-3 rounded-lg bg-nature-green px-4 py-4 font-bold text-white transition-all duration-300 hover:scale-105 hover:bg-nature-green-dark disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {formState === "loading" && (
                  <FiLoader className="animate-spin" />
                )}
                {formState === "success" && <FiCheckCircle />}
                {formState !== "loading" && formState !== "success" && (
                  <FiSend />
                )}
                <span>
                  {formState === "loading"
                    ? "Sending..."
                    : formState === "success"
                    ? "Message Sent!"
                    : "Send Message"}
                </span>
              </button>
            </form>
            {result && (
              <p
                className={`mt-4 text-center ${
                  formState === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {result}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
